// TrainLoadRecipients.jsx
import React, { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
  Box,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import VuiPagination from "components/VuiPagination";
import Icon from "@mui/material/Icon";
import { useParams } from "react-router-dom";

function TrainLoadRecipients() {
  const { site_id } = useParams();
  const [recipients, setRecipients] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [destinationDialogOpen, setDestinationDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingDestination, setEditingDestination] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    type: "customer",
    name: "",
    email: "",
    destination_ids: [],
  });
  const [destinationFormData, setDestinationFormData] = useState({
    destination_name: "",
    description: "",
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [siteName, setSiteName] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const recipientColumns = [
    { name: "Type", align: "left" },
    { name: "Name", align: "left" },
    { name: "Email", align: "left" },
    { name: "Destination", align: "left" },
    { name: "Actions", align: "center" },
  ];

  const destinationColumns = [
    { name: "Destination Name", align: "left" },
    { name: "Description", align: "left" },
    { name: "Actions", align: "center" },
  ];

  useEffect(() => {
    fetchSiteData();
    fetchData();
    fetchDestinations();
  }, [site_id]);

  const fetchSiteData = async () => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/site/profile?site_id=${site_id}`
      );
      const data = await response.json();
      setSiteName(data.name);
    } catch (error) {
      console.error("Error fetching site data:", error.message);
    }
  };

  const fetchDestinations = async () => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/train-load/destinations?site_id=${site_id}`
      );
      if (response.ok) {
        const data = await response.json();
        setDestinations(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching destinations:", error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [customers, managers] = await Promise.all([fetchCustomers(), fetchManagers()]);

      const combinedRecipients = [
        ...customers.map((customer) => ({
          ...customer,
          id: customer.customer_id,
          type: "customer",
          display_type: "Customer",
          name: customer.customer_name,
          email: customer.customer_email,
          destination:
            customer.destinations?.length > 0
              ? customer.destinations.map((d) => d.destination_name).join(", ")
              : "All Destinations",
        })),
        ...managers.map((manager) => ({
          ...manager,
          id: manager.manager_id,
          type: "manager",
          display_type: "Manager",
          name: manager.manager_name,
          email: manager.manager_email,
          destination:
            manager.destinations?.length > 0
              ? manager.destinations.map((d) => d.destination_name).join(", ")
              : "All Destinations",
        })),
      ];

      setRecipients(combinedRecipients);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/train-load/site-customers?site_id=${site_id}`
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching customers:", error);
      return [];
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/train-load/site-managers?site_id=${site_id}`
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching managers:", error);
      return [];
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        type: item.type,
        name: item.name || "",
        email: item.email || "",
        destination_ids: item.destinations ? item.destinations.map((d) => d.destination_id) : [],
      });
    } else {
      setEditingItem(null);
      setFormData({ type: "customer", name: "", email: "", destination_ids: [] });
    }
    setDialogOpen(true);
  };

  const handleOpenDestinationDialog = (destination = null) => {
    if (destination) {
      setEditingDestination(destination);
      setDestinationFormData({
        destination_name: destination.destination_name || "",
        description: destination.description || "",
      });
    } else {
      setEditingDestination(null);
      setDestinationFormData({
        destination_name: "",
        description: "",
      });
    }
    setDestinationDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData({ type: "customer", name: "", email: "", destination_ids: [] });

    setError(null);
  };

  const handleCloseDestinationDialog = () => {
    setDestinationDialogOpen(false);
    setEditingDestination(null);
    setDestinationFormData({
      destination_name: "",
      description: "",
    });
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      const isCustomer = formData.type === "customer";
      const url = isCustomer
        ? "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/train-load/site-customers"
        : "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/train-load/site-managers";

      const method = editingItem ? "PUT" : "POST";

      const payload = isCustomer
        ? {
            ...(editingItem && { customer_id: editingItem.id }),
            site_id: parseInt(site_id),
            customer_name: formData.name,
            customer_email: formData.email,
            destination_ids: formData.destination_ids, // was destination_id
          }
        : {
            ...(editingItem && { manager_id: editingItem.id }),
            site_id: parseInt(site_id),
            manager_name: formData.name,
            manager_email: formData.email,
            destination_ids: formData.destination_ids, // was destination_id
          };

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (response.ok) {
        setSuccess(
          `${isCustomer ? "Customer" : "Manager"} ${
            editingItem ? "updated" : "added"
          } successfully!`
        );
        await fetchData();
        handleCloseDialog();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorMessage = responseData.error || responseData.message || "Failed to save";
        setError(errorMessage);
      }
    } catch (error) {
      console.error("Error saving:", error);
      setError("Error saving data: " + error.message);
    }
  };

  const handleDestinationSubmit = async () => {
    try {
      setError(null);

      const url =
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/train-load/destinations";
      const method = editingDestination ? "PUT" : "POST";

      const payload = editingDestination
        ? {
            destination_id: editingDestination.destination_id,
            destination_name: destinationFormData.destination_name,
            description: destinationFormData.description,
          }
        : {
            site_id: parseInt(site_id),
            destination_name: destinationFormData.destination_name,
            description: destinationFormData.description,
          };

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (response.ok) {
        setSuccess(`Destination ${editingDestination ? "updated" : "added"} successfully!`);
        await fetchDestinations();
        handleCloseDestinationDialog();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorMessage = responseData.error || responseData.message || "Failed to save";
        setError(errorMessage);
      }
    } catch (error) {
      console.error("Error saving destination:", error);
      setError("Error saving destination: " + error.message);
    }
  };

  const handleDelete = async (item) => {
    const type = item.type;
    const id = item.id;

    if (window.confirm(`Are you sure you want to delete this ${type}?`)) {
      try {
        setError(null);
        const url = `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/train-load/site-${type}s?${type}_id=${id}`;

        const response = await fetch(url, {
          method: "DELETE",
        });

        if (response.ok) {
          setSuccess(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully!`);
          await fetchData();
          setTimeout(() => setSuccess(null), 3000);
        } else {
          const errorText = await response.text();
          setError("Failed to delete: " + errorText);
        }
      } catch (error) {
        console.error("Error deleting:", error);
        setError("Error deleting data: " + error.message);
      }
    }
  };

  const handleDeleteDestination = async (destination) => {
    if (
      window.confirm(
        `Are you sure you want to delete destination "${destination.destination_name}"?`
      )
    ) {
      try {
        setError(null);
        const url = `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/train-load/destinations?destination_id=${destination.destination_id}`;

        const response = await fetch(url, {
          method: "DELETE",
        });

        if (response.ok) {
          setSuccess("Destination deleted successfully!");
          await fetchDestinations();
          setTimeout(() => setSuccess(null), 3000);
        } else {
          const errorText = await response.text();
          setError("Failed to delete destination: " + errorText);
        }
      } catch (error) {
        console.error("Error deleting destination:", error);
        setError("Error deleting destination: " + error.message);
      }
    }
  };

  const displayedRows = recipients.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.ceil(recipients.length / rowsPerPage);

  const customerCount = recipients.filter((r) => r.type === "customer").length;
  const managerCount = recipients.filter((r) => r.type === "manager").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <VuiBox display="flex" flexDirection="column" gap={2}>
              <VuiTypography variant="lg" color="white">
                Train Load Recipients - {siteName}
              </VuiTypography>

              <VuiTypography variant="caption" color="text">
                {customerCount} Customers • {managerCount} Managers • {destinations.length}{" "}
                Destinations
              </VuiTypography>
            </VuiBox>

            <div>
              <VuiButton
                variant="contained"
                color="info"
                onClick={() => handleOpenDestinationDialog(null)}
                sx={{ mr: 2 }}
              >
                Manage Destinations
              </VuiButton>
              <VuiButton variant="contained" color="primary" onClick={() => handleOpenDialog(null)}>
                Add Recipient
              </VuiButton>
            </div>
          </VuiBox>

          {/* Alerts */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)} sx={{ m: 2 }}>
              {success}
            </Alert>
          )}

          {loading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </VuiBox>
          ) : (
            <>
              <Table
                columns={recipientColumns}
                rows={displayedRows.map((item) => ({
                  Type: (
                    <VuiTypography
                      variant="button"
                      color={item.type === "customer" ? "success" : "info"}
                      fontWeight="medium"
                    >
                      {item.display_type}
                    </VuiTypography>
                  ),
                  Name: (
                    <VuiTypography variant="button" color="white">
                      {item.name}
                    </VuiTypography>
                  ),
                  Email: (
                    <VuiTypography variant="button" color="white">
                      {item.email}
                    </VuiTypography>
                  ),
                  Destination: (
                    <VuiTypography variant="button" color="text">
                      {item.destination}
                    </VuiTypography>
                  ),
                  Actions: (
                    <div>
                      <Tooltip title={`Edit ${item.display_type}`}>
                        <IconButton color="primary" onClick={() => handleOpenDialog(item)}>
                          <Icon>edit</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Delete ${item.display_type}`}>
                        <IconButton color="error" onClick={() => handleDelete(item)}>
                          <Icon>delete</Icon>
                        </IconButton>
                      </Tooltip>
                    </div>
                  ),
                }))}
              />

              {totalPages > 1 && (
                <VuiBox display="flex" justifyContent="center" p={2}>
                  <VuiPagination>
                    <VuiPagination
                      item
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                    >
                      <Icon>keyboard_arrow_left</Icon>
                    </VuiPagination>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <VuiPagination
                        key={i + 1}
                        item
                        active={page === i + 1}
                        onClick={() => setPage(i + 1)}
                      >
                        {i + 1}
                      </VuiPagination>
                    ))}
                    <VuiPagination
                      item
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                    >
                      <Icon>keyboard_arrow_right</Icon>
                    </VuiPagination>
                  </VuiPagination>
                </VuiBox>
              )}
            </>
          )}
        </Card>
      </VuiBox>

      {/* Add/Edit Recipient Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <VuiTypography variant="lg" color="white">
            {editingItem ? "Edit" : "Add"} Recipient
          </VuiTypography>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <VuiBox component="form" display="flex" flexDirection="column" gap={2} p={2}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: "0.875rem" }}>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                inputProps={{ style: { fontSize: "0.875rem" } }}
              >
                <MenuItem value="customer" sx={{ fontSize: "0.875rem" }}>
                  Customer
                </MenuItem>
                <MenuItem value="manager" sx={{ fontSize: "0.875rem" }}>
                  Manager
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
                style: { fontSize: "0.875rem" },
              }}
              InputProps={{ style: { fontSize: "0.875rem" } }}
              sx={{
                "& .MuiOutlinedInput-root": { width: "100%" },
                "& .MuiInputBase-input": { width: "100% !important" },
              }}
            />

            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
                style: { fontSize: "0.875rem" },
              }}
              InputProps={{ style: { fontSize: "0.875rem" } }}
              sx={{
                "& .MuiOutlinedInput-root": { width: "100%" },
                "& .MuiInputBase-input": { width: "100% !important" },
              }}
            />

            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: "0.875rem" }}>Destinations</InputLabel>
              <Select
                multiple
                value={formData.destination_ids}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.includes("all")) {
                    // If all are already selected, deselect all; otherwise select all
                    setFormData({
                      ...formData,
                      destination_ids:
                        formData.destination_ids.length === destinations.length
                          ? []
                          : destinations.map((d) => d.destination_id),
                    });
                  } else {
                    setFormData({ ...formData, destination_ids: value });
                  }
                }}
                inputProps={{ style: { fontSize: "0.875rem" } }}
                renderValue={(selected) =>
                  selected.length === 0
                    ? "All Destinations"
                    : destinations
                        .filter((d) => selected.includes(d.destination_id))
                        .map((d) => d.destination_name)
                        .join(", ")
                }
              >
                <MenuItem
                  value="all"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: "bold",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  {formData.destination_ids.length === destinations.length
                    ? "Deselect All"
                    : "Select All"}
                </MenuItem>

                {destinations.map((destination) => (
                  <MenuItem
                    key={destination.destination_id}
                    value={destination.destination_id}
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {destination.destination_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </VuiBox>
        </DialogContent>
        <DialogActions>
          <VuiButton
            color="info"
            onClick={handleSubmit}
            disabled={!formData.name || !formData.email}
          >
            {editingItem ? "Update" : "Add"}
          </VuiButton>
          <VuiButton color="error" onClick={handleCloseDialog}>
            Cancel
          </VuiButton>
        </DialogActions>
      </Dialog>

      {/* Manage Destinations Dialog - UPDATED TO MATCH MAIN TABLE DESIGN */}
      <Dialog
        open={destinationDialogOpen}
        onClose={handleCloseDestinationDialog}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            color: "white",
          },
        }}
      >
        <DialogTitle>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiTypography variant="lg" color="white">
              Manage Destinations
            </VuiTypography>
            <VuiButton
              variant="contained"
              color="primary"
              onClick={() => handleOpenDestinationDialog(null)}
            >
              Add Destination
            </VuiButton>
          </VuiBox>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Destinations Table - Matching Main Table Design */}
          {destinations.length > 0 ? (
            <Table
              columns={destinationColumns}
              rows={destinations.map((destination) => ({
                "Destination Name": (
                  <VuiTypography variant="button" color="white" fontWeight="medium">
                    {destination.destination_name}
                  </VuiTypography>
                ),
                Description: (
                  <VuiTypography variant="button" color="text">
                    {destination.description || "No description"}
                  </VuiTypography>
                ),
                Actions: (
                  <div>
                    <Tooltip title="Edit Destination">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDestinationDialog(destination)}
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Destination">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteDestination(destination)}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  </div>
                ),
              }))}
            />
          ) : (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <VuiTypography variant="button" color="text">
                No destinations found. Add your first destination above.
              </VuiTypography>
            </VuiBox>
          )}

          {/* Add/Edit Destination Form - UPDATED WITH INCREASED GAP BETWEEN LABEL AND INPUT */}
          {(editingDestination || !editingDestination) && (
            <VuiBox
              component="form"
              display="flex"
              flexDirection="column"
              gap={3} // Increased gap between form elements
              mt={3}
              p={2}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderRadius: "8px",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <VuiTypography variant="h6" color="white">
                {editingDestination ? "Edit Destination" : "Add New Destination"}
              </VuiTypography>

              <VuiBox display="flex" flexDirection="column" gap={1.5}>
                {" "}
                {/* Increased gap between label and input */}
                <VuiTypography
                  variant="caption"
                  color="white"
                  fontWeight="medium"
                  sx={{ mb: 0.5 }} // Added margin bottom to create more space
                >
                  Destination Name
                </VuiTypography>
                <TextField
                  value={destinationFormData.destination_name}
                  onChange={(e) =>
                    setDestinationFormData({
                      ...destinationFormData,
                      destination_name: e.target.value,
                    })
                  }
                  fullWidth
                  required
                  placeholder="Enter destination name"
                  InputProps={{
                    style: {
                      color: "white",
                      fontSize: "0.875rem",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                    },
                    "& .MuiInputBase-input": {
                      width: "100% !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.4)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </VuiBox>

              <VuiBox display="flex" flexDirection="column" gap={1.5}>
                {" "}
                {/* Increased gap between label and input */}
                <VuiTypography
                  variant="caption"
                  color="white"
                  fontWeight="medium"
                  sx={{ mb: 0.5 }} // Added margin bottom to create more space
                >
                  Description
                </VuiTypography>
                <TextField
                  value={destinationFormData.description}
                  onChange={(e) =>
                    setDestinationFormData({
                      ...destinationFormData,
                      description: e.target.value,
                    })
                  }
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter destination description"
                  InputProps={{
                    style: {
                      color: "white",
                      fontSize: "0.875rem",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                    },
                    "& .MuiInputBase-input": {
                      width: "100% !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.4)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </VuiBox>

              <VuiBox display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                <VuiButton
                  color="info"
                  onClick={handleDestinationSubmit}
                  disabled={!destinationFormData.destination_name}
                >
                  {editingDestination ? "Update" : "Add"} Destination
                </VuiButton>
                {editingDestination && (
                  <VuiButton color="error" onClick={() => handleOpenDestinationDialog(null)}>
                    Cancel
                  </VuiButton>
                )}
              </VuiBox>
            </VuiBox>
          )}
        </DialogContent>
        <DialogActions>
          <VuiButton color="error" onClick={handleCloseDestinationDialog}>
            Close
          </VuiButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default TrainLoadRecipients;
